import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getRedirectTo } from '@/utils/authRedirect';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, Mail, Phone, Shield, Trash2, CheckCircle } from 'lucide-react';

const Account = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Change Email State
  const [newEmail, setNewEmail] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  
  // Change Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Phone State
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  const [phoneStep, setPhoneStep] = useState<'enter' | 'verify'>('enter');

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleEmailChange = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsChangingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: getRedirectTo('/account?email_change=1') }
      );

      if (error) throw error;

      toast({
        title: "Check your email",
        description: user.email 
          ? "We've sent a confirmation link to your new email address."
          : "We've sent a confirmation link to verify your email address.",
      });
      setNewEmail('');
    } catch (error: any) {
      console.error('Error changing email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update email.",
        variant: "destructive",
      });
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleResendEmailConfirmation = async () => {
    if (!user.email) return;
    
    setIsChangingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: { emailRedirectTo: getRedirectTo('/account?email_verified=1') }
      });

      if (error) throw error;

      toast({
        title: "Confirmation sent",
        description: "Check your email for the confirmation link.",
      });
    } catch (error: any) {
      console.error('Error resending confirmation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to resend confirmation.",
        variant: "destructive",
      });
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been successfully changed.",
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password.",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddPhone = async () => {
    if (!phone || phone.length < 10) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number.",
        variant: "destructive",
      });
      return;
    }

    setIsAddingPhone(true);
    try {
      const { error } = await supabase.auth.updateUser({ phone });

      if (error) throw error;

      setPhoneStep('verify');
      toast({
        title: "Verification code sent",
        description: "Check your phone for the verification code.",
      });
    } catch (error: any) {
      console.error('Error adding phone:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add phone number.",
        variant: "destructive",
      });
    } finally {
      setIsAddingPhone(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneCode || phoneCode.length < 6) {
      toast({
        title: "Invalid code",
        description: "Please enter the 6-digit verification code.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifyingPhone(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        phone,
        token: phoneCode,
        type: 'phone_change'
      });

      if (error) throw error;

      toast({
        title: "Phone verified",
        description: "Your phone number has been successfully verified.",
      });
      setPhone('');
      setPhoneCode('');
      setPhoneStep('enter');
    } catch (error: any) {
      console.error('Error verifying phone:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to verify phone number.",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const hasEmail = !!user.email;
  const emailVerified = hasEmail && !!user.email_confirmed_at;
  const hasPhone = !!user.phone;
  const phoneVerified = hasPhone && !!user.phone_confirmed_at;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Account Settings</h1>
            <p className="text-muted-foreground">Manage your account and security settings</p>
          </div>

          <Tabs defaultValue="email" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="password">Password</TabsTrigger>
              <TabsTrigger value="phone">Phone</TabsTrigger>
              <TabsTrigger value="danger">Account</TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Email Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasEmail && (
                    <div className="space-y-2">
                      <Label>Current Email</Label>
                      <div className="flex items-center gap-2">
                        <p className="text-sm">{user.email}</p>
                        <Badge variant={emailVerified ? "default" : "secondary"} className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {emailVerified ? 'Verified' : 'Unverified'}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {!emailVerified && hasEmail && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Your email needs verification. Check your inbox or resend the confirmation.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleResendEmailConfirmation}
                        disabled={isChangingEmail}
                      >
                        {isChangingEmail ? 'Sending...' : 'Resend Confirmation'}
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="new-email">
                      {hasEmail ? 'New Email' : 'Add Email'}
                    </Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder={hasEmail ? "Enter new email address" : "Enter email address"}
                    />
                  </div>

                  <Button
                    onClick={handleEmailChange}
                    disabled={isChangingEmail || !newEmail}
                  >
                    {isChangingEmail ? 'Sending...' : (hasEmail ? 'Change Email' : 'Add Email')}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="password">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Password Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Password must be at least 8 characters long.
                  </p>

                  <Button
                    onClick={handlePasswordChange}
                    disabled={isChangingPassword || !newPassword || !confirmPassword}
                  >
                    {isChangingPassword ? 'Updating...' : 'Change Password'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="phone">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Phone Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasPhone && (
                    <div className="space-y-2">
                      <Label>Current Phone</Label>
                      <div className="flex items-center gap-2">
                        <p className="text-sm">{user.phone}</p>
                        <Badge variant={phoneVerified ? "default" : "secondary"} className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {phoneVerified ? 'Verified' : 'Unverified'}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {phoneStep === 'enter' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="phone">
                          {hasPhone ? 'New Phone Number' : 'Add Phone Number'}
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1234567890"
                        />
                      </div>

                      <Button
                        onClick={handleAddPhone}
                        disabled={isAddingPhone || !phone}
                      >
                        {isAddingPhone ? 'Sending...' : (hasPhone ? 'Change Phone' : 'Add Phone')}
                      </Button>
                    </>
                  )}

                  {phoneStep === 'verify' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="phone-code">Verification Code</Label>
                        <Input
                          id="phone-code"
                          type="text"
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value)}
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setPhoneStep('enter')}
                        >
                          Back
                        </Button>
                        <Button
                          onClick={handleVerifyPhone}
                          disabled={isVerifyingPhone || !phoneCode}
                        >
                          {isVerifyingPhone ? 'Verifying...' : 'Verify Phone'}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="danger">
              <Card>
                <CardHeader>
                  <CardTitle>Account Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Sign Out</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Sign out of your account on this device.
                      </p>
                      <Button variant="outline" onClick={handleSignOut}>
                        Sign Out
                      </Button>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-medium mb-2 text-destructive">Danger Zone</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Delete your account and all associated data permanently.
                      </p>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" className="flex items-center gap-2">
                            <Trash2 className="h-4 w-4" />
                            Delete Account
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Account</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will permanently delete your account
                              and remove all your data from our servers.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete Account
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Account;